import {hash as md5} from 'spark-md5'
import {v4 as uuid} from 'uuid'

import {UserIdentifiableTraits} from '../../../auth/types/Cognito'
import {Brand} from '../../../brand/types'

interface Traits {
  user?: UserIdentifiableTraits | null
}

interface Msg {
  anonymousId: string
  userId: string
  traits?: Traits
  brandName: Brand['name']
}

// NOTE: This is inspired by Segment normalize method
// Reference: https://github.com/FindHotel/analytics.js/blob/findhotel-master/analytics.js#L3380
const normalize = (msg: Msg) => {
  const id = String(md5(`${JSON.stringify(msg)}${String(uuid())}`))
  const {anonymousId, userId, traits, brandName} = msg

  return {
    anonymousId,
    userId,
    traits,
    context: {
      brandName,
      platform: 'web',
      userAgent: navigator.userAgent,
      app: {name: 'website', version: '1.0.0'},
      library: {
        name: 'daedalus-client',
        version: '1.0.0'
      }
    },
    sentAt: Date.now(),
    timestamp: Date.now(),
    messageId: `ajs-${id}`,
    type: 'identify',
    version: 1
  }
}

interface CustomIdentityParams {
  anonymousId: string
  userId: string
  customerDataEndpoint: string
  customerDataApiKey: string
  brandName: Brand['name']
  cognitoTraits?: UserIdentifiableTraits | null
}

/**
 * Sends user identity data to the customer data endpoint
 * @param params - The identity parameters
 * @param params.anonymousId - Anonymous user identifier
 * @param params.userId - User identifier
 * @param params.customerDataEndpoint - Endpoint URL for customer data
 * @param params.customerDataApiKey - API key for authentication
 * @param params.brandName - Brand name
 * @param [params.cognitoTraits] - Cognito user traits
 */
export const customIdentify = async ({
  anonymousId,
  userId,
  customerDataEndpoint,
  customerDataApiKey,
  brandName,
  cognitoTraits
}: CustomIdentityParams) => {
  if (!customerDataEndpoint) return

  if (!userId && !anonymousId) {
    throw new Error('Empty userId and anonymousId')
  }

  const traits: Traits = {user: cognitoTraits}

  const data = normalize({
    anonymousId,
    userId,
    traits,
    brandName
  })

  const response = await fetch(customerDataEndpoint, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': customerDataApiKey
    }
  })

  if (!response.ok) {
    throw new Error(
      `customIdentify failed: ${response.status} ${response.statusText}`
    )
  }

  return response
}
